import { warehouseClient } from "@/infrastructure/http/warehouseClient";
import type { Adjustment, AdjustmentListResponse, CreateAdjustmentInput, AddAdjustmentLineInput, ApiEnvelope } from "@/domain/types";
import axios from "axios";

function getLocale(): string {
  try { return localStorage.getItem("nexo-locale") ?? "es"; } catch { return "es"; }
}

const ERROR_TRANSLATIONS: Record<string, string> = {
  "Only draft adjustments can be edited or submitted": "Only draft adjustments can be edited or submitted",
  "Only pending adjustments can be approved or rejected": "Only pending adjustments can be approved or rejected",
  "Only approved adjustments can be voided": "Only approved adjustments can be voided",
  "Adjustment must have at least one detail line": "Adjustment must have at least one detail line",
  "Reference document is required to submit for approval": "Reference document is required to submit for approval",
  "Justification is required for each line": "Justification is required for each line",
  "Approver must be different from the creator": "Approver must be different from the creator",
  "Rejection reason is required": "Rejection reason is required",
  "Void reason is required": "Void reason is required",
  "Stock insuficiente": "Insufficient stock",
  "Adjustment not found": "Adjustment not found",
};

function translateError(msg: string): string {
  if (getLocale() === "es") return msg;
  for (const [es, en] of Object.entries(ERROR_TRANSLATIONS)) {
    if (msg.includes(es)) return msg.replace(es, en);
  }
  return msg;
}

function extractMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return "Error";
  const data = err.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as Record<string, unknown>).message;
    if (typeof msg === "string") return translateError(msg);
    if (Array.isArray(msg) && msg.length > 0) return translateError(String(msg[0]));
  }
  return "Error";
}

export function isAdjustmentError(err: unknown): err is Error & { name: "AdjustmentError" } {
  return err instanceof Error && err.name === "AdjustmentError";
}

function throwAdjustmentError(err: unknown): never {
  const msg = extractMessage(err);
  const e = new Error(msg);
  e.name = "AdjustmentError";
  throw e;
}

export async function listAdjustments(params: { page?: number; limit?: number; status?: string; warehouseId?: string; from?: string; to?: string }): Promise<AdjustmentListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  if (params.warehouseId) query.set("warehouseId", params.warehouseId);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  const response = await warehouseClient.get<ApiEnvelope<AdjustmentListResponse>>(`/adjustment?${query.toString()}`);
  return response.data.data;
}

export async function getAdjustment(id: string): Promise<Adjustment> {
  const response = await warehouseClient.get<ApiEnvelope<Adjustment>>(`/adjustment/${id}`);
  return response.data.data;
}

export async function createAdjustment(input: CreateAdjustmentInput): Promise<Adjustment> {
  try {
    const response = await warehouseClient.post<ApiEnvelope<Adjustment>>("/adjustment", input);
    return response.data.data;
  } catch (err) { throwAdjustmentError(err); }
}

export async function updateAdjustment(id: string, input: Partial<Pick<CreateAdjustmentInput, "reason" | "referenceDoc" | "notes">>): Promise<void> {
  try {
    await warehouseClient.patch(`/adjustment/${id}`, input);
  } catch (err) { throwAdjustmentError(err); }
}

export async function addAdjustmentLine(adjustmentId: string, input: AddAdjustmentLineInput): Promise<void> {
  try {
    await warehouseClient.post(`/adjustment/${adjustmentId}/lines`, input);
  } catch (err) { throwAdjustmentError(err); }
}

export async function updateAdjustmentLine(adjustmentId: string, lineId: string, input: Partial<AddAdjustmentLineInput>): Promise<void> {
  try {
    await warehouseClient.patch(`/adjustment/${adjustmentId}/lines/${lineId}`, input);
  } catch (err) { throwAdjustmentError(err); }
}

export async function deleteAdjustmentLine(adjustmentId: string, lineId: string): Promise<void> {
  try {
    await warehouseClient.delete(`/adjustment/${adjustmentId}/lines/${lineId}`);
  } catch (err) { throwAdjustmentError(err); }
}

export async function submitAdjustment(id: string): Promise<void> {
  try {
    await warehouseClient.post(`/adjustment/${id}/submit`);
  } catch (err) { throwAdjustmentError(err); }
}

export async function approveAdjustment(id: string, comment?: string): Promise<void> {
  try {
    await warehouseClient.post(`/adjustment/${id}/approve`, { comment: comment || undefined });
  } catch (err) { throwAdjustmentError(err); }
}

export async function rejectAdjustment(id: string, reason: string): Promise<void> {
  try {
    await warehouseClient.post(`/adjustment/${id}/reject`, { reason });
  } catch (err) { throwAdjustmentError(err); }
}

export async function voidAdjustment(id: string, reason: string): Promise<void> {
  try {
    await warehouseClient.post(`/adjustment/${id}/void`, { reason });
  } catch (err) { throwAdjustmentError(err); }
}
